import { type ReduxApplicationState } from 'src/redux/combinedReducer';
import { type SortOption } from 'src/redux/productsQuery/types/SortOption';
import {
  type GuaranteedArrivalDay,
  GUARANTEED_ARRIVAL_DAYS,
} from 'src/utils/guaranteedArrivalDays';
import { getListingsSortAsURLSearchParams } from '../listings-api/getListingsSortAsURLSearchParams';

type StoreToPageURLState = Pick<ReduxApplicationState, 'productsQuery'>;

export type BuildListingsQueryParamsOptions = {
  /** Written as given. Resolve it against the chosen date first, as `deriveListingsURLParts` does. */
  guaranteedArrivalDay?: GuaranteedArrivalDay;
  page?: number;
  sortOption?: SortOption | null;
};

export const appendListingsQueryParams = (
  urlParams: URLSearchParams,
  state: StoreToPageURLState,
  options: BuildListingsQueryParamsOptions = {},
): void => {
  const {
    productsQuery: { searchTerm, sortOptions = [] },
  } = state;
  const { guaranteedArrivalDay, page, sortOption = undefined } = options;
  const activeSortOption =
    sortOption === undefined ? sortOptions.find((option) => option.active) : sortOption;

  if (activeSortOption?.key) {
    getListingsSortAsURLSearchParams(urlParams, activeSortOption);
  }

  // don't include page=1 as that is the default
  if (page && page > 1) {
    urlParams.set('page', page.toString());
  }

  if (searchTerm) {
    urlParams.set('term', searchTerm);
  }

  if (guaranteedArrivalDay) {
    urlParams.set('guaranteed', GUARANTEED_ARRIVAL_DAYS[guaranteedArrivalDay].urlValue);
  }
};

/**
 * Returns non-facet query params only (sort, page, term, guaranteed).
 * Facet filters are serialised into the URL path by the product-listings-urls `build`.
 */
export const buildListingsQueryParams = (
  state: StoreToPageURLState,
  options: BuildListingsQueryParamsOptions = {},
): URLSearchParams => {
  const urlParams = new URLSearchParams();
  appendListingsQueryParams(urlParams, state, options);
  return urlParams;
};
